import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Tab,
  Tabs,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  Stack,
  TextField,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveDialog from './ResponsiveDialog';
import { getAllPersons, getAllStringings, getAllStringTypes, getAllRacquets, createRacquet, createString } from '../api';
import { Person, StringType, StringingEntry, Racquet } from '../types';
import { getPersonFullName } from '../util/helpers';
import CreateStringingForm from '../components/CreateStringingForm';
import CreateStringTypeForm from './CreateStringTypeForm';
import CreateRacquetForm from './CreateRacquetsForm';
import StringsTable from './StringsTable';
import RacquetsTable from './RacquetsTable';
import { useNavigate } from 'react-router-dom';

export default function MainPage() {
  const [tab, setTab] = useState(0);
  const [showForm, setShowForm] = useState(false);

  const [stringings, setStringings] = useState<StringingEntry[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [stringTypes, setStringTypes] = useState<StringType[]>([]);
  const [racquets, setRacquets] = useState<Racquet[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'strungAt' | 'person'>('strungAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const rowsPerPage = 100;
  const [reloadKey, setReloadKey] = useState(0);
  const [openStringDialog, setOpenStringDialog] = useState(false);
  const [openRacquetDialog, setOpenRacquetDialog] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      const [p, s, r, str] = await Promise.all([
        getAllPersons(),
        getAllStringTypes(),
        getAllRacquets(),
        getAllStringings()
      ]);
      setPersons(p);
      setStringTypes(s);
      setRacquets(r);
      setStringings(str);
    }
    fetchData();
  }, [reloadKey]);

  const filteredStringings = stringings.filter((s) => {
    const person = persons.find((p) => p.id === s.personId);
    const fullName = person ? `${person.firstName} ${person.lastName}`.toLowerCase() : '';
    return fullName.includes(search.toLowerCase());
  });

  const sortedStringings = [...filteredStringings].sort((a, b) => {
    let aValue: string | number = '';
    let bValue: string | number = '';

    if (sortBy === 'person') {
      aValue = getPersonFullName(a.personId, persons).toLowerCase();
      bValue = getPersonFullName(b.personId, persons).toLowerCase();
    } else {
      aValue = a.strungAt ? new Date(a.strungAt).getTime() : 0;
      bValue = b.strungAt ? new Date(b.strungAt).getTime() : 0;
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;  // fallback
  });

  const paginatedStringings = useMemo(
    () => sortedStringings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedStringings, page]
  );

  const handleSort = (field: 'strungAt' | 'person') => {
    setPage(0);
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleCreated = () => {
    setReloadKey((prev) => prev + 1);
    setShowForm(false);
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4 }}>
      <Box textAlign="center">
        <img src="/logos/String.png" alt="String Theories" style={{ width: '80%', maxWidth: '400px' }} />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab} onChange={(_, newValue) => setTab(newValue)} centered>
          <Tab label="Stringings" />
          <Tab label="Strings" />
          <Tab label="Racquets" />
        </Tabs>
      </Box>

      {tab === 0 && (
        <>
          {!showForm && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <TextField
                label="Search by name"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                size="small"
                sx={{ flex: 1, mr: 2 }}
              />
              <IconButton
                onClick={() => setShowForm(true)}
                sx={{
                  borderRadius: '50%',
                  backgroundColor: '#282c34',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    backgroundColor: '#405a52',
                    '& svg': {
                      transform: 'scale(1.2)',  // Grow icon on hover
                    },
                  },
                  '& svg': {
                    transition: 'transform 0.2s ease-in-out',  // Smooth transition
                  },
                }}
              >
                <AddIcon sx={{ color: '#fff' }} />
              </IconButton>
            </Stack>
          )}

          {showForm && (
            <ResponsiveDialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm">
              <Box px={2}>
                <Typography variant="h6" mb={2} pt={0}>
                  Add stringing
                </Typography>
                <CreateStringingForm
                  onSave={handleCreated}
                  onCancel={() => setShowForm(false)}
                  persons={persons}
                  stringTypes={stringTypes}
                  title="Add Stringing"
                />
              </Box>
            </ResponsiveDialog>
          )}

          {!showForm && (
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'strungAt'}
                        direction={sortOrder}
                        onClick={() => handleSort('strungAt')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'person'}
                        direction={sortOrder}
                        onClick={() => handleSort('person')}
                      >
                        Person
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Racquet</TableCell>
                    <TableCell>Strings</TableCell>
                    <TableCell>Tension</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStringings.map((s) => {
                    const personName = getPersonFullName(s.personId, persons);
                    const racquetName = s.racquet ? `${s.racquet.brand} ${s.racquet.model}` : 'Unknown';

                    const mainsStr = s.mains ? `${s.mains.brand} ${s.mains.name}` : '';
                    const crossesStr = s.crosses ? `${s.crosses.brand} ${s.crosses.name}` : '';

                    const stringsDisplay =
                      s.mainsId === s.crossesId
                        ? mainsStr
                        : `${mainsStr} | ${crossesStr}`;

                    const tensionDisplay =
                      s.mainsTension === s.crossesTension
                        ? `${s.mainsTension} lbs`
                        : `${s.mainsTension} | ${s.crossesTension} lbs`;

                    return (
                      <TableRow
                        key={s.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/person/${s.personId}`)}
                      >
                        <TableCell>{s.strungAt ? new Date(s.strungAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : 'N/A'}</TableCell>
                        <TableCell sx={{ textWrap: 'nowrap' }}>{personName}</TableCell>
                        <TableCell sx={{ textWrap: 'nowrap' }}>{racquetName}</TableCell>
                        <TableCell sx={{ textWrap: 'nowrap' }}>{stringsDisplay}</TableCell>
                        <TableCell sx={{ textWrap: 'nowrap' }}>{tensionDisplay}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={sortedStringings.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <StringsTable
            stringTypes={stringTypes}
            stringings={stringings}
            onAddNewString={() => setOpenStringDialog(true)}
            onUpdated={() => setReloadKey(prev => prev + 1)}
          />
          <ResponsiveDialog open={openStringDialog} onClose={() => setOpenStringDialog(false)} maxWidth="sm">
            <Box p={2}>
              <Typography variant="h6" mb={2}>
                Add string
              </Typography>
              <CreateStringTypeForm
                onSave={async (data) => {
                  await createString(data);
                  setOpenStringDialog(false);
                  setReloadKey(prev => prev + 1);
                }}
                onCancel={() => setOpenStringDialog(false)}
              />
            </Box>
          </ResponsiveDialog>
        </>
      )}

      {tab === 2 && (
        <>
          <RacquetsTable
            racquets={racquets}
            stringings={stringings}
            onAddNewRacquet={() => setOpenRacquetDialog(true)}
            onUpdated={() => setReloadKey(prev => prev + 1)}
          />
          <ResponsiveDialog open={openRacquetDialog} onClose={() => setOpenRacquetDialog(false)} maxWidth="sm">
            <Box p={2}>
              <Typography variant="h6" mb={2}>
                New racquet
              </Typography>
              <CreateRacquetForm
                onSave={async (data) => {
                  await createRacquet(data);
                  setOpenRacquetDialog(false);
                  setReloadKey(prev => prev + 1);
                }}
                onCancel={() => setOpenRacquetDialog(false)}
              />
            </Box>
          </ResponsiveDialog>
        </>
      )}
    </Container>
  );
}
